import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ProductOrigin {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductOriginFormData {
  name: string;
  sort_order?: number;
}

const QUERY_KEY = ['product-origins'];

export function useProductOrigins() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all origins
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ProductOrigin[]> => {
      const { data, error } = await supabase
        .from('product_origins')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as ProductOrigin[];
    },
  });

  // Create origin
  const createMutation = useMutation({
    mutationFn: async (input: ProductOriginFormData) => {
      // Get max sort_order to auto-assign next value
      const { data: existing } = await supabase
        .from('product_origins')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      const maxSortOrder = existing?.[0]?.sort_order ?? -1;

      const { data, error } = await supabase
        .from('product_origins')
        .insert({
          name: input.name,
          sort_order: input.sort_order ?? maxSortOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Origen creado',
        description: 'El origen se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo crear el origen: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update origin
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: ProductOriginFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('product_origins')
        .update({
          name: input.name,
          sort_order: input.sort_order,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Origen actualizado',
        description: 'El origen se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar el origen: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete origin
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('product_origins')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Origen eliminado',
        description: 'El origen se ha eliminado correctamente.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el origen. Verifica que no tenga productos asociados.',
        variant: 'destructive',
      });
    },
  });

  // Reorder origins with optimistic update
  const reorderMutation = useMutation({
    mutationFn: async ({
      originId,
      newSortOrder,
      swapOriginId,
      swapSortOrder,
    }: {
      originId: string;
      newSortOrder: number;
      swapOriginId: string;
      swapSortOrder: number;
    }) => {
      const [result1, result2] = await Promise.all([
        supabase
          .from('product_origins')
          .update({ sort_order: newSortOrder })
          .eq('id', originId),
        supabase
          .from('product_origins')
          .update({ sort_order: swapSortOrder })
          .eq('id', swapOriginId),
      ]);

      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previousOrigins = queryClient.getQueryData<ProductOrigin[]>(QUERY_KEY);

      queryClient.setQueryData<ProductOrigin[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        
        const updated = [...old];
        const idx1 = updated.findIndex(o => o.id === variables.originId);
        const idx2 = updated.findIndex(o => o.id === variables.swapOriginId);
        
        if (idx1 !== -1 && idx2 !== -1) {
          [updated[idx1], updated[idx2]] = [updated[idx2], updated[idx1]];
          updated[idx1] = { ...updated[idx1], sort_order: variables.swapSortOrder };
          updated[idx2] = { ...updated[idx2], sort_order: variables.newSortOrder };
        }
        
        return updated;
      });

      return { previousOrigins };
    },
    onError: (err, variables, context) => {
      if (context?.previousOrigins) {
        queryClient.setQueryData(QUERY_KEY, context.previousOrigins);
      }
      toast({
        title: 'Error',
        description: 'No se pudo reordenar el origen',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY, refetchType: 'all' });
    },
  });

  return {
    origins: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createOrigin: createMutation.mutateAsync,
    updateOrigin: updateMutation.mutateAsync,
    deleteOrigin: deleteMutation.mutateAsync,
    reorderOrigin: reorderMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
  };
}
